// i18n bootstrap — auto-detects browser/device language and falls back to en.
//
// Supported locales (must mirror the keys present in ./locales/*.json):
//   en (fallback) · es · pt · hi · id · de · ru · fr · ja · zh
//
// Detection order: querystring (?lng=xx) → localStorage → navigator.language.
// In dev we expose a floating language switcher (DevLanguageSwitcher) that
// writes to the same `i18nextLng` localStorage key, so toggling there persists
// across reloads exactly like a manual locale pick would.
import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

import en from "./locales/en.json";
import es from "./locales/es.json";
import pt from "./locales/pt.json";
import hi from "./locales/hi.json";
import id from "./locales/id.json";
import de from "./locales/de.json";
import ru from "./locales/ru.json";
import fr from "./locales/fr.json";
import ja from "./locales/ja.json";
import zhCN from "./locales/zh-CN.json";
import zhTW from "./locales/zh-TW.json";

export const SUPPORTED_LOCALES = [
  "en", "es", "pt", "hi", "id", "de", "ru", "fr", "ja", "zh-CN", "zh-TW",
] as const;
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

// Human-readable names for the dev language switcher (and any future UI picker).
export const LOCALE_LABELS: Record<SupportedLocale, string> = {
  en: "English",
  es: "Español",
  pt: "Português",
  hi: "हिन्दी",
  id: "Bahasa Indonesia",
  de: "Deutsch",
  ru: "Русский",
  fr: "Français",
  ja: "日本語",
  "zh-CN": "简体中文",
  "zh-TW": "繁體中文",
};

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
      es: { translation: es },
      pt: { translation: pt },
      hi: { translation: hi },
      id: { translation: id },
      de: { translation: de },
      ru: { translation: ru },
      fr: { translation: fr },
      ja: { translation: ja },
      "zh-CN": { translation: zhCN },
      "zh-TW": { translation: zhTW },
    },
    fallbackLng: {
      // Browser may report "zh", "zh-HK", "zh-SG", etc. Map to the closest
      // script we ship before falling back to English.
      zh: ["zh-CN", "en"],
      "zh-HK": ["zh-TW", "en"],
      "zh-MO": ["zh-TW", "en"],
      "zh-SG": ["zh-CN", "en"],
      // Common regional variants for the other languages we ship — listed
      // explicitly because we intentionally do NOT enable
      // `nonExplicitSupportedLngs` (it would collapse "zh-CN" → "zh" and
      // wipe out our Chinese variants entirely, sending users to English).
      "pt-BR": ["pt", "en"],
      "pt-PT": ["pt", "en"],
      "es-MX": ["es", "en"],
      "es-ES": ["es", "en"],
      "es-AR": ["es", "en"],
      "en-US": ["en"],
      "en-GB": ["en"],
      "de-AT": ["de", "en"],
      "de-CH": ["de", "en"],
      "fr-CA": ["fr", "en"],
      "fr-FR": ["fr", "en"],
      default: ["en"],
    },
    supportedLngs: SUPPORTED_LOCALES as unknown as string[],
    load: "currentOnly",
    interpolation: { escapeValue: false }, // React already escapes
    detection: {
      order: ["querystring", "localStorage", "navigator"],
      lookupQuerystring: "lng",
      lookupLocalStorage: "i18nextLng",
      caches: ["localStorage"],
    },
    returnNull: false,
  });

// Sync the document's <html lang> attribute so screen readers and CSS
// :lang() selectors get the right hint.
i18n.on("languageChanged", (lng) => {
  if (typeof document !== "undefined") {
    document.documentElement.lang = lng;
  }
});

export default i18n;
